import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonComponent } from '../../../../components/ui/button/button';
import {
  PreferencesService,
  PreferenceCategoryMeta,
  PreferenceOption,
} from '../../../../shared/services/preferences.service';

interface OptionGroup {
  type: string;
  options: PreferenceOption[];
}

@Component({
  selector: 'app-preference-options',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonComponent],
  templateUrl: './preference-options.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreferenceOptions implements OnInit, OnDestroy {

  constructor(
    public preferencesService: PreferencesService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) { }

  meta?: PreferenceCategoryMeta;
  groups: OptionGroup[] = [];

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('category') ?? '';
    this.meta = this.preferencesService.metaFor(slug);

    // Landed here directly (e.g. a bookmarked/shared link) without the
    // draft loaded from the profile yet, or with an unknown slug — bounce
    // back to the main Preferences screen, which loads the profile and
    // offers the real category list.
    if (!this.meta || !this.preferencesService.loaded) {
      this.router.navigate(['/dashboard/preferences']);
      return;
    }

    const options = this.preferencesService.optionsFor(this.meta.key);
    const byType = new Map<string, PreferenceOption[]>();
    for (const opt of options) {
      if (!byType.has(opt.type)) byType.set(opt.type, []);
      byType.get(opt.type)!.push(opt);
    }
    this.groups = [...byType.entries()].map(([type, opts]) => ({ type, options: opts }));
  }

  isSelected(activity: string): boolean {
    return this.meta ? this.preferencesService.isSelected(this.meta.key, activity) : false;
  }

  toggle(activity: string): void {
    if (!this.meta) return;
    this.preferencesService.toggleValue(this.meta.key, activity);
    this.cdr.markForCheck();
  }

  get selectedCount(): number {
    return this.meta ? this.preferencesService.draft[this.meta.key].length : 0;
  }

  /** Auto-save state shown under the panel title. */
  get saveStatus() {
    return this.preferencesService.saveStatus();
  }

  ngOnDestroy(): void {
    this.preferencesService.flush();
  }

  goBack(): void {
    this.router.navigate(['/dashboard/preferences']);
  }
}
